// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");

import { DisclosureTrigger } from '@/components/remote/primitives/Disclosure';
import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { ActivityItem } from '@web/lib/remoteActivityModel';
import type {
  AgentExecutionForest,
  AgentExecutionNode,
  AgentExecutionState,
} from '@web/lib/remoteAgentProjection';
import {
  Ban,
  Bot,
  Check,
  CircleHelp,
  Clock3,
  FileText,
  Loader2,
  Pause,
  Users,
  Wrench,
} from 'lucide';
import { useId, useMemo, useState } from 'react';

const STATE_LABEL: Record<AgentExecutionState, string> = {
  queued: 'Queued',
  running: 'Working',
  waiting: 'Waiting',
  completed: 'Finished',
  failed: 'Blocked',
  interrupted: 'Interrupted',
};

const STATE_CLASS: Record<AgentExecutionState, string> = {
  queued: 'bg-ds-bg-neutral-subtle-default text-ds-icon-neutral-muted-default',
  running:
    'bg-ds-bg-status-running-subtle-default text-ds-text-status-running-strong-default',
  waiting:
    'bg-ds-bg-status-pending-subtle-default text-ds-text-status-pending-strong-default',
  completed:
    'bg-ds-bg-status-completed-subtle-default text-ds-text-status-completed-strong-default',
  failed:
    'bg-ds-bg-status-error-subtle-default text-ds-text-status-error-strong-default',
  interrupted:
    'bg-ds-bg-status-paused-subtle-default text-ds-text-status-paused-strong-default',
};

function AgentStateIcon({ state }: { state: AgentExecutionState }) {
  let icon = Clock3;
  let className = 'h-3.5 w-3.5';
  if (state === 'running') {
    icon = Loader2;
    className += ' animate-spin';
  } else if (state === 'waiting') {
    icon = CircleHelp;
  } else if (state === 'completed') {
    icon = Check;
  } else if (state === 'failed') {
    icon = Ban;
  } else if (state === 'interrupted') {
    icon = Pause;
  }
  return (
    <span
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
        STATE_CLASS[state]
      )}
      role="img"
      aria-label={STATE_LABEL[state]}
      title={STATE_LABEL[state]}
    >
      <MorphIcon icon={icon} className={className} />
    </span>
  );
}

function ExchangeSection({
  label,
  contents,
  empty,
}: {
  label: string;
  contents: string[];
  empty: string;
}) {
  return (
    <section className="rounded-xl bg-ds-bg-neutral-subtle-default px-3 py-2.5">
      <p className="m-0 mb-1 text-label-xs font-medium uppercase tracking-wide text-ds-text-neutral-muted-default">
        {label}
      </p>
      {contents.length > 0 ? (
        <div className="flex min-w-0 flex-col gap-2">
          {contents.map((content, index) => (
            <RemoteMarkdown key={`${label}:${index}`} content={content} />
          ))}
        </div>
      ) : (
        <p className="m-0 text-body-sm text-ds-text-neutral-muted-default">
          {empty}
        </p>
      )}
    </section>
  );
}

function ToolSummary({
  node,
  forest,
}: {
  node: AgentExecutionNode;
  forest: AgentExecutionForest;
}) {
  const tools = node.toolCallIds
    .map((id) => forest.toolsById[id])
    .filter(Boolean);
  if (tools.length === 0) return null;
  return (
    <section>
      <p className="m-0 mb-1.5 text-label-xs font-medium uppercase tracking-wide text-ds-text-neutral-muted-default">
        Tools
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {tools.map((tool) => (
          <li
            key={tool.id}
            className="flex min-w-0 items-start gap-2 rounded-lg px-2 py-1.5 text-body-sm text-ds-text-neutral-default-default"
          >
            <MorphIcon
              icon={/document|file|pdf/i.test(tool.name) ? FileText : Wrench}
              className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
            />
            <span className="min-w-0 flex-1">
              <span className="font-medium">{tool.name}</span>
              {tool.detail ? (
                <span className="ml-1 text-ds-text-neutral-muted-default">
                  {tool.detail}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-label-xs text-ds-text-neutral-muted-default">
              {STATE_LABEL[tool.state]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AgentActivity({
  node,
  itemsById,
}: {
  node: AgentExecutionNode;
  itemsById: Map<string, ActivityItem>;
}) {
  const activities = node.itemIds
    .map((id) => itemsById.get(id))
    .filter(
      (item): item is ActivityItem =>
        Boolean(item) && item?.kind !== 'agent' && item?.kind !== 'tool'
    );
  if (activities.length === 0) return null;
  return (
    <section>
      <p className="m-0 mb-1.5 text-label-xs font-medium uppercase tracking-wide text-ds-text-neutral-muted-default">
        Activity
      </p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {activities.map((item) => (
          <li key={item.id} className="flex min-w-0 items-start gap-2 px-2">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ds-icon-neutral-muted-default" />
            <span className="min-w-0 text-body-sm">
              <span className="font-medium text-ds-text-neutral-default-default">
                {item.title}
              </span>
              {item.detail ? (
                <span className="ml-1 text-ds-text-neutral-muted-default">
                  {item.detail}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function hasOpenDescendant(
  id: string,
  forest: AgentExecutionForest,
  seen = new Set<string>()
): boolean {
  if (seen.has(id)) return false;
  seen.add(id);
  const node = forest.nodesById[id];
  if (!node) return false;
  if (
    node.state === 'running' ||
    node.state === 'waiting' ||
    node.state === 'failed'
  ) {
    return true;
  }
  return node.childIds.some((childId) =>
    hasOpenDescendant(childId, forest, seen)
  );
}

type AgentGroup =
  | { kind: 'single'; id: string }
  | { kind: 'parallel'; id: string; ids: string[] };

function groupAgents(
  ids: string[],
  forest: AgentExecutionForest
): AgentGroup[] {
  const groups: AgentGroup[] = [];
  const grouped = new Map<string, string[]>();
  for (const id of ids) {
    const parallelId = forest.nodesById[id]?.parallelGroupId;
    if (!parallelId) {
      groups.push({ kind: 'single', id });
      continue;
    }
    const members = grouped.get(parallelId);
    if (members) members.push(id);
    else {
      const created = [id];
      grouped.set(parallelId, created);
      groups.push({ kind: 'parallel', id: parallelId, ids: created });
    }
  }
  return groups.map((group) =>
    group.kind === 'parallel' && group.ids.length === 1
      ? { kind: 'single', id: group.ids[0] }
      : group
  );
}

function AgentList({
  ids,
  forest,
  itemsById,
  depth,
  overrides,
  onToggle,
}: {
  ids: string[];
  forest: AgentExecutionForest;
  itemsById: Map<string, ActivityItem>;
  depth: number;
  overrides: Record<string, boolean>;
  onToggle: (id: string, expanded: boolean) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {groupAgents(ids, forest).map((group) =>
        group.kind === 'single' ? (
          <AgentAccordion
            key={group.id}
            id={group.id}
            forest={forest}
            itemsById={itemsById}
            depth={depth}
            overrides={overrides}
            onToggle={onToggle}
          />
        ) : (
          <section
            key={`parallel:${group.id}`}
            className="rounded-xl bg-ds-bg-neutral-subtle-default p-2"
            aria-label={`${group.ids.length} agents running in parallel`}
          >
            <div className="mb-2 flex items-center gap-2 px-1 text-label-xs font-medium text-ds-text-neutral-muted-default">
              <MorphIcon icon={Users} className="h-4 w-4" />
              <span>Parallel · {group.ids.length} agents</span>
            </div>
            <div
              className={cn(
                'grid min-w-0 grid-cols-1 gap-2',
                depth === 0 && 'lg:grid-cols-2'
              )}
            >
              {group.ids.map((id) => (
                <AgentAccordion
                  key={id}
                  id={id}
                  forest={forest}
                  itemsById={itemsById}
                  depth={depth}
                  overrides={overrides}
                  onToggle={onToggle}
                />
              ))}
            </div>
          </section>
        )
      )}
    </div>
  );
}

function AgentAccordion({
  id,
  forest,
  itemsById,
  depth,
  overrides,
  onToggle,
}: {
  id: string;
  forest: AgentExecutionForest;
  itemsById: Map<string, ActivityItem>;
  depth: number;
  overrides: Record<string, boolean>;
  onToggle: (id: string, expanded: boolean) => void;
}) {
  const disclosureId = `agent-execution-${useId().replace(/:/g, '')}`;
  const node = forest.nodesById[id];
  if (!node) return null;
  const autoOpen = hasOpenDescendant(id, forest);
  const expanded = overrides[id] ?? autoOpen;
  const assignments = node.assignmentIds
    .map((messageId) => forest.messagesById[messageId]?.content)
    .filter((content): content is string => Boolean(content));
  const responses = node.responseIds
    .map((messageId) => forest.messagesById[messageId]?.content)
    .filter((content): content is string => Boolean(content));

  return (
    <section className="min-w-0">
      <div className="flex min-w-0 gap-3">
        <div className="flex w-7 shrink-0 flex-col items-center">
          <AgentStateIcon state={node.state} />
          {expanded && (
            <span
              className={cn(
                'my-1 min-h-4 w-px flex-1 bg-ds-border-neutral-subtle-default',
                node.state === 'running' &&
                  'bg-ds-bg-status-running-default-default'
              )}
              aria-hidden
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <DisclosureTrigger
            expanded={expanded}
            onExpandedChange={(next) => onToggle(id, next)}
            ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            controlsId={disclosureId}
            className="min-h-7 p-0"
          >
            <div className="flex min-w-0 items-start gap-2">
              <MorphIcon
                icon={Bot}
                className="mt-0.5 h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
              />
              <div className="min-w-0 flex-1">
                <p className="m-0 truncate text-body-sm font-medium text-ds-text-neutral-default-default">
                  {node.name}
                </p>
                <p className="m-0 mt-0.5 truncate text-label-xs text-ds-text-neutral-muted-default">
                  {node.role || STATE_LABEL[node.state]}
                  {node.toolCallIds.length > 0
                    ? ` · ${node.toolCallIds.length} tool${node.toolCallIds.length === 1 ? '' : 's'}`
                    : ''}
                  {node.childIds.length > 0
                    ? ` · ${node.childIds.length} sub-agent${node.childIds.length === 1 ? '' : 's'}`
                    : ''}
                </p>
              </div>
            </div>
          </DisclosureTrigger>

          {expanded && (
            <div
              id={disclosureId}
              className="mt-3 flex min-w-0 flex-col gap-3 pb-2"
            >
              <ExchangeSection
                label="Query input"
                contents={assignments}
                empty="Assignment details were not recorded for this event."
              />
              <ToolSummary node={node} forest={forest} />
              <AgentActivity node={node} itemsById={itemsById} />
              <ExchangeSection
                label="Query response"
                contents={responses}
                empty={
                  node.state === 'running' || node.state === 'queued'
                    ? 'Working…'
                    : node.state === 'waiting'
                      ? 'Waiting before a response can continue.'
                      : 'No response was recorded.'
                }
              />
              {node.childIds.length > 0 && (
                <section className="min-w-0 pt-1">
                  <p className="m-0 mb-2 text-label-xs font-medium uppercase tracking-wide text-ds-text-neutral-muted-default">
                    Sub-agents
                  </p>
                  <AgentList
                    ids={node.childIds}
                    forest={forest}
                    itemsById={itemsById}
                    depth={depth + 1}
                    overrides={overrides}
                    onToggle={onToggle}
                  />
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function AgentExecutionForestView({
  forest,
  items,
}: {
  forest: AgentExecutionForest;
  items: ActivityItem[];
}) {
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  if (forest.rootIds.length === 0) return null;

  return (
    <section
      className="mb-3 min-w-0"
      aria-label={
        forest.mode === 'workforce'
          ? 'Workforce agent execution tree'
          : 'Agent execution'
      }
    >
      <AgentList
        ids={forest.rootIds}
        forest={forest}
        itemsById={itemsById}
        depth={0}
        overrides={overrides}
        onToggle={(id, expanded) =>
          setOverrides((current) => ({ ...current, [id]: expanded }))
        }
      />
    </section>
  );
}
